import React, { useCallback, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaChartPie,
  FaMapMarkerAlt,
  FaBuilding,
  FaSearchDollar,
  FaVenusMars,
  FaCamera,
  FaChartLine,
  FaSpinner,
} from "react-icons/fa";
import api from "../../services/axios";
import { formatarDia, formatarDiaHora } from "../../services/conversaoTimer";
import ListaTabelaModal from "../../components/listaTabelaModal";
import "../../components/modal/modal.css";

const USO_IMAGEM_NAO_AUTORIZADOS_COLS = [
  { key: "nome", label: "Nome", excelLabel: "Nome" },
  {
    key: "empresa",
    label: "Empresa",
    excelLabel: "Empresa",
    excelValue: (row) => row.empresa || "",
    render: (row) =>
      row.empresa ? (
        row.empresa
      ) : (
        <span className="ltm-muted">Não informada</span>
      ),
  },
  { key: "email", label: "E-mail", excelLabel: "E-mail" },
  {
    key: "tipo",
    label: "Tipo",
    excelLabel: "Tipo",
    excelValue: (row) =>
      row.tipo === "participante_patrocinador"
        ? "Participante patrocinador"
        : "Convidado",
    render: (row) =>
      row.tipo === "participante_patrocinador"
        ? "Participante patrocinador"
        : "Convidado",
  },
];

const COMPOSICAO_AGENDA_PENDENTES_COLS = [
  { key: "nome", label: "Nome", excelLabel: "Nome" },
  {
    key: "empresa",
    label: "Empresa",
    excelLabel: "Empresa",
    excelValue: (row) => row.empresa || "",
    render: (row) =>
      row.empresa ? (
        row.empresa
      ) : (
        <span className="ltm-muted">Não informada</span>
      ),
  },
  {
    key: "cargo",
    label: "Cargo",
    excelLabel: "Cargo",
    excelValue: (row) => row.cargo || "",
    render: (row) =>
      row.cargo ? row.cargo : <span className="ltm-muted">—</span>,
  },
  { key: "email", label: "E-mail", excelLabel: "E-mail" },
  {
    key: "tokenExpiraEm",
    label: "Expira em",
    excelLabel: "Token expira em",
    excelValue: (row) =>
      row.tokenExpiraEm ? formatarDiaHora(row.tokenExpiraEm) : "",
    render: (row) =>
      row.tokenExpiraEm ? (
        formatarDiaHora(row.tokenExpiraEm)
      ) : (
        <span className="ltm-muted">—</span>
      ),
  },
];

const PALETTE = [
  "#0d1d41",
  "#008c39",
  "#2563eb",
  "#0ea5e9",
  "#64748b",
  "#94a3b8",
  "#1e3a5f",
  "#059669",
  "#475569",
  "#38bdf8",
];

const COMPOSICAO_COLORS = {
  participantes: "#0d1d41",
  patrocinadores: "#008c39",
};

const USO_IMAGEM_COLORS = {
  sim: "#008c39",
  nao: "#64748b",
  nao_informado: "#cbd5e1",
};

function fmt(n) {
  return Number(n ?? 0).toLocaleString("pt-BR");
}

function ContagemTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div className="adb-chart-tooltip">
      <p className="adb-chart-tooltip-title">{item.label}</p>
      <p>
        Quantidade: <strong>{fmt(item.quantidade)}</strong>
      </p>
      {item.percentual != null ? (
        <p>
          Participação: <strong>{item.percentual}%</strong>
        </p>
      ) : null}
    </div>
  );
}

function AcumuladoTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div className="adb-chart-tooltip">
      <p className="adb-chart-tooltip-title">{label}</p>
      <p>
        Acumulado: <strong>{fmt(item.acumuladoTotal)}</strong>
      </p>
      <p>
        Participantes: <strong>{fmt(item.acumuladoParticipantes)}</strong>
      </p>
      <p>
        Patrocinadores: <strong>{fmt(item.acumuladoPatrocinadores)}</strong>
      </p>
      <p className="adb-chart-tooltip-hint">
        Novos no dia: {fmt(item.total)}
      </p>
    </div>
  );
}

function withPercentual(items) {
  const total =
    (items ?? []).reduce((acc, item) => acc + Number(item.quantidade ?? 0), 0) ||
    1;

  return (items ?? []).map((item) => ({
    ...item,
    quantidade: Number(item.quantidade ?? 0),
    percentual: Math.round((Number(item.quantidade ?? 0) / total) * 100),
  }));
}

function ChartCard({ title, subtitle, icon, children, empty, className = "", footer }) {
  return (
    <div className={`apl-card adb-chart-card adb-stats-card ${className}`}>
      <div className="adb-chart-header">
        <div>
          <h3>
            {icon ? (
              <span className="adb-stats-card-icon" aria-hidden>
                {icon}
              </span>
            ) : null}
            {title}
          </h3>
          {subtitle ? <p className="adb-chart-subtitle">{subtitle}</p> : null}
        </div>
      </div>
      {empty ? (
        <p className="adb-empty-chart">Sem dados suficientes para este gráfico.</p>
      ) : (
        children
      )}
      {footer}
    </div>
  );
}

function DonutChartCard({
  title,
  subtitle,
  icon,
  data,
  colorMap,
  centerLabel = "total",
  onItemClick,
  clickableChaves,
}) {
  const chartData = useMemo(() => withPercentual(data), [data]);
  const total = chartData.reduce((acc, item) => acc + item.quantidade, 0);
  const hasData = total > 0;
  const clickableSet = useMemo(
    () => new Set(clickableChaves ?? []),
    [clickableChaves],
  );

  const handleSliceClick = (sliceData) => {
    const item = sliceData?.payload ?? sliceData;
    if (!onItemClick || !item?.chave) {
      return;
    }
    if (clickableSet.size > 0 && !clickableSet.has(item.chave)) {
      return;
    }
    onItemClick(item);
  };

  const handleLegendClick = (entry) => {
    handleSliceClick(entry);
  };

  return (
    <ChartCard title={title} subtitle={subtitle} icon={icon} empty={!hasData}>
      <div
        className={`adb-stats-donut-wrap${
          onItemClick ? " adb-stats-donut-wrap--interactive" : ""
        }`}
        role="img"
        aria-label={`${title}. Total ${fmt(total)}.`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="quantidade"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
              onClick={handleSliceClick}
              cursor={onItemClick ? "pointer" : undefined}
            >
              {chartData.map((item, index) => (
                <Cell
                  key={item.chave}
                  fill={
                    colorMap?.[item.chave] ?? PALETTE[index % PALETTE.length]
                  }
                  cursor={
                    onItemClick &&
                    (clickableSet.size === 0 || clickableSet.has(item.chave))
                      ? "pointer"
                      : undefined
                  }
                />
              ))}
            </Pie>
            <Tooltip content={<ContagemTooltip />} />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              onClick={onItemClick ? handleLegendClick : undefined}
              formatter={(value, entry) => {
                const chave = entry?.payload?.chave;
                const isClickable =
                  onItemClick &&
                  (clickableSet.size === 0 || clickableSet.has(chave));
                return (
                  <span
                    className={
                      isClickable ? "adb-stats-legend-clickable" : undefined
                    }
                    title={
                      isClickable
                        ? `Ver detalhes de ${value}`
                        : undefined
                    }
                  >
                    {`${value} (${fmt(entry?.payload?.quantidade ?? 0)})`}
                  </span>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="adb-acao-center" aria-hidden>
          <span className="adb-acao-center-value">{fmt(total)}</span>
          <span className="adb-acao-center-label">{centerLabel}</span>
        </div>
      </div>
    </ChartCard>
  );
}

function HorizontalBarCard({
  title,
  subtitle,
  icon,
  data,
  maxItems = 10,
  footer,
}) {
  const chartData = useMemo(() => {
    const ranked = withPercentual(data)
      .filter((item) => item.quantidade > 0)
      .slice(0, maxItems);
    return ranked;
  }, [data, maxItems]);

  const hasData = chartData.length > 0;
  const chartHeight = Math.max(220, chartData.length * 36 + 40);

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      icon={icon}
      empty={!hasData}
      footer={footer}
    >
      <div
        className="adb-stats-hbar-wrap"
        style={{ height: chartHeight }}
        role="img"
        aria-label={title}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke="#e2e8f0"
            />
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={110}
              tick={{ fill: "#475569", fontSize: 11, fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<ContagemTooltip />} cursor={{ fill: "rgba(13, 29, 65, 0.04)" }} />
            <Bar dataKey="quantidade" radius={[0, 6, 6, 0]} maxBarSize={22}>
              {chartData.map((item, index) => (
                <Cell key={item.chave} fill={PALETTE[index % PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function UltimosOutrosProcuraList({ items }) {
  const lista = Array.isArray(items) ? items : [];

  const formatarIdentificacao = (item) =>
    [item.nome, item.cargo, item.empresa].filter(Boolean).join(" · ");

  return (
    <div className="adb-stats-outros-procura" aria-label="Descrições de Outros na procura SBF">
      <h4 className="adb-stats-outros-procura-title">
        “Outros” informados ({lista.length})
      </h4>
      {lista.length > 0 ? (
        <ul className="adb-stats-outros-procura-list">
          {lista.map((item) => {
            const identificacao = formatarIdentificacao(item);
            return (
              <li key={item.id} className="adb-stats-outros-procura-item">
                <p className="adb-stats-outros-procura-desc" title={item.descricao}>
                  {item.descricao}
                </p>
                <div className="adb-stats-outros-procura-meta">
                  <span
                    className="adb-stats-outros-procura-nome"
                    title={identificacao}
                  >
                    {identificacao}
                  </span>
                  <time dateTime={item.credenciadoEm}>
                    {formatarDia(item.credenciadoEm)}
                  </time>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="adb-stats-outros-procura-empty">
          Nenhuma descrição de “Outros” registrada ainda.
        </p>
      )}
    </div>
  );
}

function VerticalBarCard({ title, subtitle, icon, data }) {
  const chartData = useMemo(
    () => withPercentual(data).filter((item) => item.quantidade > 0),
    [data],
  );
  const hasData = chartData.length > 0;

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      icon={icon}
      empty={!hasData}
      className="adb-stats-card--tall"
    >
      <div className="adb-stats-vbar-wrap" role="img" aria-label={title}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 48 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />
            <XAxis
              dataKey="label"
              interval={0}
              angle={-28}
              textAnchor="end"
              height={60}
              tick={{ fill: "#64748b", fontSize: 11, fontWeight: 600 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<ContagemTooltip />} cursor={{ fill: "rgba(13, 29, 65, 0.04)" }} />
            <Bar dataKey="quantidade" radius={[6, 6, 0, 0]} maxBarSize={40}>
              {chartData.map((item, index) => (
                <Cell key={item.chave} fill={PALETTE[index % PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function AcumuladoAreaCard({ data }) {
  const chartData = useMemo(() => data ?? [], [data]);
  const hasData = chartData.length > 0;

  return (
    <ChartCard
      title="Evolução acumulada"
      subtitle="Crescimento do total de credenciados ao longo dos dias"
      icon={<FaChartLine size={14} />}
      empty={!hasData}
      className="adb-stats-card--wide"
    >
      <div
        className="adb-stats-area-wrap"
        role="img"
        aria-label="Gráfico de área da evolução acumulada de credenciamentos"
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <defs>
              <linearGradient id="adbAcumuladoFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d1d41" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#0d1d41" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />
            <XAxis
              dataKey="dia"
              tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={8}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip content={<AcumuladoTooltip />} />
            <Area
              type="monotone"
              dataKey="acumuladoTotal"
              name="Acumulado"
              stroke="#0d1d41"
              strokeWidth={2.5}
              fill="url(#adbAcumuladoFill)"
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function InsightStrip({ estatisticas, acaoEspecial, onAbrirComposicaoPendentes }) {
  const total = Number(estatisticas?.baseTotal ?? 0);
  const participantes = Number(estatisticas?.baseParticipantes ?? 0);
  const patrocinadores = Math.max(0, total - participantes);

  const topEstado = [...(estatisticas?.porEstado ?? [])]
    .filter((item) => item.chave !== "nao_informado" && item.quantidade > 0)
    .sort((a, b) => b.quantidade - a.quantidade)[0];

  const topSegmento = [...(estatisticas?.porSegmento ?? [])]
    .filter((item) => item.chave !== "nao_informado" && item.quantidade > 0)
    .sort((a, b) => b.quantidade - a.quantidade)[0];

  const topProcura = [...(estatisticas?.porProcura ?? [])]
    .filter((item) => item.chave !== "nao_informado" && item.quantidade > 0)
    .sort((a, b) => b.quantidade - a.quantidade)[0];

  const interesse =
    (acaoEspecial?.opcoes ?? []).find((item) => item.opcao === "sim")
      ?.quantidade ?? 0;

  const pctPatrocinadores =
    total > 0 ? Math.round((patrocinadores / total) * 100) : 0;

  const composicaoAgenda = estatisticas?.composicaoAgenda ?? {
    comToken: 0,
    concluidos: 0,
    percentual: 0,
  };
  const comToken = Number(composicaoAgenda.comToken ?? 0);
  const concluidos = Number(composicaoAgenda.concluidos ?? 0);
  const pctComposicao = Number(composicaoAgenda.percentual ?? 0);
  const pendentes = Math.max(0, comToken - concluidos);
  const podeAbrirPendentes =
    typeof onAbrirComposicaoPendentes === "function" && comToken > 0;

  const insights = [
    {
      label: "Mix do público",
      value:
        total > 0
          ? `${pctPatrocinadores}% patrocinadores`
          : "Sem credenciados",
      hint:
        total > 0
          ? `${fmt(participantes)} participantes · ${fmt(patrocinadores)} patrocinadores`
          : "Aguardando primeiros cadastros",
    },
    {
      label: "Estado líder",
      value: topEstado ? topEstado.label : "—",
      hint: topEstado
        ? `${fmt(topEstado.quantidade)} participantes`
        : "Sem dados de UF",
    },
    {
      label: "Segmento dominante",
      value: topSegmento ? topSegmento.label : "—",
      hint: topSegmento
        ? `${fmt(topSegmento.quantidade)} participantes`
        : "Sem dados de segmento",
    },
    {
      label: "Maior procura no SBF",
      value: topProcura ? topProcura.label : "—",
      hint: topProcura
        ? `${fmt(topProcura.quantidade)} indicações`
        : "Sem dados de procura",
    },
    {
      label: "Interesse na ação especial",
      value: fmt(interesse),
      hint: acaoEspecial?.titulo ?? "Ação especial",
    },
  ];

  const insightComposicao = {
    label: "Composição de agenda",
    value:
      comToken > 0
        ? `${pctComposicao}% concluíram`
        : "Sem tokens gerados",
    hint:
      comToken > 0
        ? pendentes > 0
          ? `${fmt(pendentes)} pendentes · clique para ver a lista`
          : `${fmt(concluidos)} de ${fmt(comToken)} concluíram · clique para conferir`
        : "Aguardando geração de links de composição",
  };

  return (
    <div className="adb-stats-insights-wrap">
      <div className="adb-stats-insights" aria-label="Destaques analíticos">
        {insights.map((item) => (
          <article key={item.label} className="adb-stats-insight">
            <p className="adb-stats-insight-label">{item.label}</p>
            <p className="adb-stats-insight-value">{item.value}</p>
            <p className="adb-stats-insight-hint">{item.hint}</p>
          </article>
        ))}
      </div>
      <div
        className="adb-stats-insights adb-stats-insights--secondary"
        aria-label="Composição de agenda"
      >
        <article
          className={`adb-stats-insight${
            podeAbrirPendentes ? " adb-stats-insight--clickable" : ""
          }`}
          role={podeAbrirPendentes ? "button" : undefined}
          tabIndex={podeAbrirPendentes ? 0 : undefined}
          onClick={podeAbrirPendentes ? onAbrirComposicaoPendentes : undefined}
          onKeyDown={
            podeAbrirPendentes
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onAbrirComposicaoPendentes();
                  }
                }
              : undefined
          }
          title={
            podeAbrirPendentes
              ? "Ver convidados que ainda não concluíram a composição"
              : undefined
          }
        >
          <p className="adb-stats-insight-label">{insightComposicao.label}</p>
          <p className="adb-stats-insight-value">{insightComposicao.value}</p>
          <p className="adb-stats-insight-hint">{insightComposicao.hint}</p>
        </article>
      </div>
    </div>
  );
}

export const ESTATISTICAS_VAZIAS = {
  baseParticipantes: 0,
  baseTotal: 0,
  composicao: [],
  composicaoAgenda: {
    comToken: 0,
    concluidos: 0,
    percentual: 0,
  },
  porSexo: [],
  porRegiao: [],
  porEstado: [],
  porSegmento: [],
  porFaturamento: [],
  porProcura: [],
  ultimosOutrosProcura: [],
  usoImagem: [],
  acumuladoPorDia: [],
};

export default function AdmDashEstatisticas({ estatisticas, acaoEspecial }) {
  const dados = estatisticas ?? ESTATISTICAS_VAZIAS;
  const [modalUsoImagem, setModalUsoImagem] = useState(false);
  const [usuariosSemAutorizacao, setUsuariosSemAutorizacao] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const [modalComposicao, setModalComposicao] = useState(false);
  const [composicaoPendentes, setComposicaoPendentes] = useState([]);
  const [composicaoLoading, setComposicaoLoading] = useState(false);
  const [composicaoError, setComposicaoError] = useState(null);

  const abrirNaoAutorizados = useCallback(async () => {
    setModalUsoImagem(true);
    setModalLoading(true);
    setModalError(null);
    setUsuariosSemAutorizacao([]);

    try {
      const resp = await api.get("/dashboard-adm-uso-imagem-nao-autorizados");
      setUsuariosSemAutorizacao(resp.data?.usuarios ?? []);
    } catch (err) {
      console.error("Erro ao carregar usuários sem autorização de imagem:", err);
      setModalError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os usuários sem autorização de imagem.",
      );
    } finally {
      setModalLoading(false);
    }
  }, []);

  const fecharModalUsoImagem = useCallback(() => {
    setModalUsoImagem(false);
    setModalError(null);
  }, []);

  const handleUsoImagemClick = useCallback(
    (item) => {
      if (item?.chave === "nao") {
        abrirNaoAutorizados();
      }
    },
    [abrirNaoAutorizados],
  );

  const abrirComposicaoPendentes = useCallback(async () => {
    setModalComposicao(true);
    setComposicaoLoading(true);
    setComposicaoError(null);
    setComposicaoPendentes([]);

    try {
      const resp = await api.get("/dashboard-adm-composicao-agenda-pendentes");
      setComposicaoPendentes(resp.data?.usuarios ?? []);
    } catch (err) {
      console.error("Erro ao carregar composição de agenda pendente:", err);
      setComposicaoError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os convidados pendentes.",
      );
    } finally {
      setComposicaoLoading(false);
    }
  }, []);

  const fecharModalComposicao = useCallback(() => {
    setModalComposicao(false);
    setComposicaoError(null);
  }, []);

  return (
    <section
      className="adb-section adb-tab-panel"
      id="adb-panel-estatisticas"
      role="tabpanel"
      aria-labelledby="adb-tab-estatisticas"
    >
      <div className="adb-stats-intro">
        <h3 className="adb-section-title">Análise do credenciamento</h3>
        <p className="adb-stats-intro-text">
          Perfil demográfico e institucional dos participantes credenciados,
          composição do público e ritmo de adesão — base para leitura crítica
          do cadastro.
        </p>
      </div>

      <InsightStrip
        estatisticas={dados}
        acaoEspecial={acaoEspecial}
        onAbrirComposicaoPendentes={abrirComposicaoPendentes}
      />

      <div className="adb-stats-grid adb-stats-grid--top">
        <AcumuladoAreaCard data={dados.acumuladoPorDia} />
        <DonutChartCard
          title="Composição do público"
          subtitle="Participantes vs patrocinadores credenciados"
          icon={<FaChartPie size={14} />}
          data={dados.composicao}
          colorMap={COMPOSICAO_COLORS}
          centerLabel="credenciados"
        />
      </div>

      <div className="adb-stats-grid">
        <HorizontalBarCard
          title="Por estado"
          subtitle="Top UFs entre participantes"
          icon={<FaMapMarkerAlt size={14} />}
          data={dados.porEstado}
          maxItems={12}
        />
        <HorizontalBarCard
          title="Por região"
          subtitle="Distribuição geográfica dos participantes"
          icon={<FaMapMarkerAlt size={14} />}
          data={dados.porRegiao}
          maxItems={8}
        />
      </div>

      <div className="adb-stats-grid">
        <VerticalBarCard
          title="Segmento institucional"
          subtitle="Tipo de instituição dos participantes"
          icon={<FaBuilding size={14} />}
          data={dados.porSegmento}
        />
        <VerticalBarCard
          title="Faixa de faturamento"
          subtitle="Porte declarado da instituição"
          icon={<FaSearchDollar size={14} />}
          data={dados.porFaturamento}
        />
      </div>

      <div className="adb-stats-grid">
        <HorizontalBarCard
          title="O que procura no SBF"
          subtitle="Interesse comercial declarado no credenciamento"
          icon={<FaSearchDollar size={14} />}
          data={dados.porProcura}
          maxItems={10}
          footer={
            <UltimosOutrosProcuraList items={dados.ultimosOutrosProcura} />
          }
        />
        <div className="adb-stats-grid adb-stats-grid--stack">
          <DonutChartCard
            title="Sexo"
            subtitle="Todos os credenciados"
            icon={<FaVenusMars size={14} />}
            data={dados.porSexo}
            centerLabel="respostas"
          />
          <DonutChartCard
            title="Uso de imagem"
            subtitle="Clique em “Não autorizou” para ver a lista"
            icon={<FaCamera size={14} />}
            data={dados.usoImagem}
            colorMap={USO_IMAGEM_COLORS}
            centerLabel="respostas"
            onItemClick={handleUsoImagemClick}
            clickableChaves={["nao"]}
          />
        </div>
      </div>

      <ListaTabelaModal
        show={modalUsoImagem}
        onClose={fecharModalUsoImagem}
        title="Uso de imagem — Não autorizou"
        subtitle="Credenciados que não autorizaram o uso de imagem."
        countLabel="Sem autorização"
        columns={USO_IMAGEM_NAO_AUTORIZADOS_COLS}
        rows={usuariosSemAutorizacao}
        loading={modalLoading}
        error={modalError}
        emptyMessage="Nenhum usuário com uso de imagem não autorizado."
        loadingMessage="Carregando usuários…"
        excelFileName={`uso_imagem_nao_autorizados_${new Date().toISOString().slice(0, 10)}`}
        excelSheetName="Não autorizados"
        getRowHref={(row) => `/private/adm/convidados/editar/${row.id}`}
        size="xl"
      />

      <ListaTabelaModal
        show={modalComposicao}
        onClose={fecharModalComposicao}
        title="Composição de agenda — Pendentes"
        subtitle="Convidados com token de composição gerado que ainda não concluíram as escolhas."
        countLabel="Pendentes"
        columns={COMPOSICAO_AGENDA_PENDENTES_COLS}
        rows={composicaoPendentes}
        loading={composicaoLoading}
        error={composicaoError}
        emptyMessage="Nenhum convidado pendente de composição de agenda."
        loadingMessage="Carregando pendentes…"
        excelFileName={`composicao_agenda_pendentes_${new Date().toISOString().slice(0, 10)}`}
        excelSheetName="Pendentes"
        getRowHref={(row) => `/private/adm/convidados/editar/${row.id}`}
        size="xl"
      />
    </section>
  );
}
